/**
 * The download of the Pocket voice languages the user wants
 * (`useWantedPocketLanguages`), as the Settings Voice row shows it.
 *
 * Shown only while a wanted language is downloading or failed; nothing once
 * they are all installed. Rust owns the download, so this joins the one
 * provisioning started (or starts it) and reports how far along it is.
 * `retry` asks again after a failure.
 */
import { useCallback, useEffect, useState } from "react";

import {
  ensurePocketVoice,
  onPocketVoiceProgress,
  pocketVoiceStatus,
  type PocketLanguage,
} from "./pocket-voice";
import { useWantedPocketLanguages } from "./use-wanted-pocket-languages";
import { percentComplete } from "./voice-assets";

export type PocketVoiceDownload =
  | { kind: "hidden" }
  | { kind: "downloading"; language: PocketLanguage; percent: number }
  | { kind: "failed"; language: PocketLanguage };

const HIDDEN: PocketVoiceDownload = { kind: "hidden" };

/** `active`: the Denker voices speak here (not a system engine). */
export function usePocketVoiceDownload(active: boolean): {
  download: PocketVoiceDownload;
  retry: () => void;
} {
  const wanted = useWantedPocketLanguages();
  const [download, setDownload] = useState<PocketVoiceDownload>(HIDDEN);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!active || !wanted) {
      setDownload(HIDDEN);
      return;
    }
    let cancelled = false;
    let stopListening = () => {};
    void (async () => {
      for (const language of wanted) {
        const status = await pocketVoiceStatus(language);
        if (cancelled) return;
        if (status.ready) continue;
        setDownload({ kind: "downloading", language, percent: 0 });
        stopListening = onPocketVoiceProgress((progress) => {
          if (cancelled || progress.language !== language) return;
          setDownload({
            kind: "downloading",
            language,
            percent: percentComplete(
              progress.downloaded_bytes,
              progress.total_bytes,
            ),
          });
        });
        const after = await ensurePocketVoice(language).catch(() => null);
        stopListening();
        stopListening = () => {};
        if (cancelled) return;
        if (!after?.ready) {
          setDownload({ kind: "failed", language });
          return;
        }
      }
      if (!cancelled) setDownload(HIDDEN);
    })();
    return () => {
      cancelled = true;
      stopListening();
    };
  }, [active, wanted, attempt]);

  const retry = useCallback(() => setAttempt((count) => count + 1), []);
  return { download, retry };
}
